import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Appointment,
  Company,
  CompanyCustomerSummary,
  AddOn,
  PricingTier,
  Venue,
  CompanyUser
} from '@/types';
import {
  AlertCircle,
  Search,
  Plus,
  Check,
  Edit2,
  Mail,
  X,
  Calendar,
  CreditCard,
  FileText,
  MapPin,
  Users,
  UserPlus,
  AlertTriangle,
  ShieldCheck,
  Key,
  Trash2,
  Loader2,
  MessageSquare,
  ArrowDown,
  ArrowUp,
  ArrowUpDown
} from 'lucide-react';
import { api } from '@/services/api';
import { showAlertModal, showConfirmModal } from '@shared/components/common/AlertModal';
import { Pagination } from '@shared/components/common/Pagination';
import { createId } from '@/lib/ids';
import { StatusBadge } from '@shared/components/common/StatusBadge';
import { isValidUkPhone, normalizeUkPhone } from '@/lib/phone';
import { isValidEmail } from '@/lib/formatters';
import { formatUkDate, formatUkDateTime } from '@/lib/dates';
import { normalizeErrorMessage } from '@/lib/errorUtils';
import { buildMapById } from '@/lib/collections';

interface CompaniesTabProps {
  venues: Venue[];
  addOns: AddOn[];
  onAddCompany: ((company: Company) => void) | undefined;
  onApproveCompany:
    | ((
        companyId: string,
        tier: PricingTier,
        paymentPref: 'invoice' | 'card',
        requirePo: boolean
      ) => void)
    | undefined;
  onRejectCompany: ((companyId: string) => void) | undefined;
  onUpdateCompany: ((company: Company) => void) | undefined;
  onDeleteCompany: ((companyId: string) => Promise<void> | void) | undefined;
  canEdit: boolean;
  onPendingCountChange?: (count: number) => void;
}

type CompanyStatus = Company['status'];
const isCompanyStatus = (value: string): value is CompanyStatus => {
  return value === 'pending' || value === 'active' || value === 'on_hold' || value === 'rejected';
};

export const CompaniesTab: React.FC<CompaniesTabProps> = ({
  venues,
  addOns,
  onAddCompany,
  onApproveCompany,
  onRejectCompany,
  onUpdateCompany,
  onDeleteCompany,
  canEdit,
  onPendingCountChange
}) => {
  const [search, setSearch] = useState('');
  const [companySort, setCompanySort] = useState<{
    sortBy: 'createdAt' | 'name';
    sortDir: 'asc' | 'desc';
  }>({ sortBy: 'createdAt', sortDir: 'desc' });
  const [approvingCompany, setApprovingCompany] = useState<Company | null>(null);
  const [rejectingCompany, setRejectingCompany] = useState<Company | null>(null);
  const [editingCompany, setEditingCompany] = useState<Company | null>(null);
  const [companyPhoneTouched, setCompanyPhoneTouched] = useState(false);
  const [viewingTeamCompany, setViewingTeamCompany] = useState<Company | null>(null);
  const [viewingActivityCompany, setViewingActivityCompany] = useState<Company | null>(null);
  const [activityTab, setActivityTab] = useState<'bookings' | 'customers'>('bookings');
  const [isDeletingMemberId, setIsDeletingMemberId] = useState<string | null>(null);
  const [isDeletingCompanyId, setIsDeletingCompanyId] = useState<string | null>(null);
  const [resettingMemberId, setResettingMemberId] = useState<string | null>(null);

  const [companies, setCompanies] = useState<Company[]>([]);
  const [isLoadingCompanies, setIsLoadingCompanies] = useState(false);
  const [companiesError, setCompaniesError] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [totalCount, setTotalCount] = useState(0);

  const [pendingCompanies, setPendingCompanies] = useState<Company[]>([]);
  const [isLoadingPending, setIsLoadingPending] = useState(false);
  const [pendingError, setPendingError] = useState('');
  const [pendingPage, setPendingPage] = useState(1);
  const [pendingPageSize, setPendingPageSize] = useState(25);
  const [pendingTotalCount, setPendingTotalCount] = useState(0);

  const [teamMembers, setTeamMembers] = useState<CompanyUser[]>([]);
  const [teamPage, setTeamPage] = useState(1);
  const [teamPageSize, setTeamPageSize] = useState(25);
  const [teamTotalCount, setTeamTotalCount] = useState(0);
  const [isLoadingTeam, setIsLoadingTeam] = useState(false);
  const [teamError, setTeamError] = useState('');
  const [isAddingTeamMember, setIsAddingTeamMember] = useState(false);
  const [newMember, setNewMember] = useState({
    name: '',
    email: '',
    receiveNotifications: true
  });
  const addOnsById = useMemo(() => buildMapById(addOns), [addOns]);
  const [memberError, setMemberError] = useState('');
  const [isSavingMember, setIsSavingMember] = useState(false);
  const [editingTeamMember, setEditingTeamMember] = useState<CompanyUser | null>(null);
  const [teamEditError, setTeamEditError] = useState('');
  const [isSavingTeamEdit, setIsSavingTeamEdit] = useState(false);

  const [activityBookings, setActivityBookings] = useState<Appointment[]>([]);
  const [activityBookingsPage, setActivityBookingsPage] = useState(1);
  const [activityBookingsPageSize, setActivityBookingsPageSize] = useState(25);
  const [activityBookingsTotalCount, setActivityBookingsTotalCount] = useState(0);
  const [isLoadingActivityBookings, setIsLoadingActivityBookings] = useState(false);
  const [activityBookingsError, setActivityBookingsError] = useState('');

  const [activityCustomers, setActivityCustomers] = useState<CompanyCustomerSummary[]>([]);
  const [activityCustomersPage, setActivityCustomersPage] = useState(1);
  const [activityCustomersPageSize, setActivityCustomersPageSize] = useState(25);
  const [activityCustomersTotalCount, setActivityCustomersTotalCount] = useState(0);
  const [isLoadingActivityCustomers, setIsLoadingActivityCustomers] = useState(false);
  const [activityCustomersError, setActivityCustomersError] = useState('');
  const [activityCustomerSearch, setActivityCustomerSearch] = useState('');
  const canRenderPortal = typeof document !== 'undefined';

  // State for approval/rejection options
  const [selectedTier, setSelectedTier] = useState<PricingTier>('bronze');
  const [selectedPaymentPref, setSelectedPaymentPref] = useState<'invoice' | 'card'>('invoice');
  const [selectedRequirePo, setSelectedRequirePo] = useState(false);
  const normalizeEditableCompany = (company: Company): Company => ({
    ...company,
    paymentPreference: company.paymentPreference || 'invoice',
    tier: company.tier || 'bronze',
    status: company.status || 'active',
    requirePo: Boolean(company.requirePo)
  });

  useEffect(() => {
    setPage(1);
  }, [search, companySort.sortBy, companySort.sortDir]);

  useEffect(() => {
    setCompanyPhoneTouched(false);
  }, [editingCompany?.id]);

  useEffect(() => {
    let isActive = true;
    setIsLoadingCompanies(true);
    setCompaniesError('');
    api
      .listStaffCompanies({
        page,
        pageSize,
        q: search || undefined,
        status: 'active,on_hold,rejected',
        sortBy: companySort.sortBy,
        sortDir: companySort.sortDir
      })
      .then((data) => {
        if (!isActive) return;
        setCompanies(data.items || []);
        setTotalCount(Number(data.totalCount || 0));
      })
      .catch((err: unknown) => {
        if (!isActive) return;
        setCompanies([]);
        setTotalCount(0);
        setCompaniesError(normalizeErrorMessage(err, 'Failed to load companies.'));
      })
      .finally(() => {
        if (isActive) setIsLoadingCompanies(false);
      });

    return () => {
      isActive = false;
    };
  }, [page, pageSize, search, companySort.sortBy, companySort.sortDir]);

  useEffect(() => {
    let isActive = true;
    setIsLoadingPending(true);
    setPendingError('');
    api
      .listStaffCompanies({
        page: pendingPage,
        pageSize: pendingPageSize,
        status: 'pending'
      })
      .then((data) => {
        if (!isActive) return;
        setPendingCompanies(data.items || []);
        setPendingTotalCount(Number(data.totalCount || 0));
      })
      .catch((err: unknown) => {
        if (!isActive) return;
        setPendingCompanies([]);
        setPendingTotalCount(0);
        setPendingError(normalizeErrorMessage(err, 'Failed to load pending companies.'));
      })
      .finally(() => {
        if (isActive) setIsLoadingPending(false);
      });

    return () => {
      isActive = false;
    };
  }, [pendingPage, pendingPageSize]);

  useEffect(() => {
    if (onPendingCountChange) {
      onPendingCountChange(pendingTotalCount);
    }
  }, [pendingTotalCount, onPendingCountChange]);

  useEffect(() => {
    if (viewingTeamCompany?.id) {
      setTeamPage(1);
      setIsAddingTeamMember(false);
      setMemberError('');
      setNewMember({ name: '', email: '', receiveNotifications: true });
      setEditingTeamMember(null);
      setTeamEditError('');
      return;
    }
    setTeamMembers([]);
    setTeamTotalCount(0);
    setTeamError('');
  }, [viewingTeamCompany?.id]);

  useEffect(() => {
    if (!viewingTeamCompany?.id) return;
    let isActive = true;
    setIsLoadingTeam(true);
    setTeamError('');
    api
      .listCompanyTeam(viewingTeamCompany.id, { page: teamPage, pageSize: teamPageSize })
      .then((data) => {
        if (!isActive) return;
        setTeamMembers(data.items || []);
        setTeamTotalCount(Number(data.totalCount || 0));
      })
      .catch((err: unknown) => {
        if (!isActive) return;
        setTeamMembers([]);
        setTeamTotalCount(0);
        setTeamError(normalizeErrorMessage(err, 'Failed to load company team.'));
      })
      .finally(() => {
        if (isActive) setIsLoadingTeam(false);
      });

    return () => {
      isActive = false;
    };
  }, [viewingTeamCompany?.id, teamPage, teamPageSize]);

  useEffect(() => {
    setTeamEditError('');
  }, [editingTeamMember?.id]);

  useEffect(() => {
    if (viewingActivityCompany?.id) {
      setActivityTab('bookings');
      setActivityBookingsPage(1);
      setActivityCustomersPage(1);
      setActivityCustomerSearch('');
      return;
    }
    setActivityBookings([]);
    setActivityBookingsTotalCount(0);
    setActivityBookingsError('');
    setActivityCustomers([]);
    setActivityCustomersTotalCount(0);
    setActivityCustomersError('');
  }, [viewingActivityCompany?.id]);

  useEffect(() => {
    if (!viewingActivityCompany?.id || activityTab !== 'bookings') return;
    let isActive = true;
    setIsLoadingActivityBookings(true);
    setActivityBookingsError('');
    api
      .listStaffBookings({
        page: activityBookingsPage,
        pageSize: activityBookingsPageSize,
        companyId: viewingActivityCompany.id,
        sortBy: 'date',
        sortDir: 'desc'
      })
      .then((data) => {
        if (!isActive) return;
        setActivityBookings(data.items || []);
        setActivityBookingsTotalCount(Number(data.totalCount || 0));
      })
      .catch((err: unknown) => {
        if (!isActive) return;
        setActivityBookings([]);
        setActivityBookingsTotalCount(0);
        setActivityBookingsError(normalizeErrorMessage(err, 'Failed to load bookings.'));
      })
      .finally(() => {
        if (isActive) setIsLoadingActivityBookings(false);
      });

    return () => {
      isActive = false;
    };
  }, [viewingActivityCompany?.id, activityTab, activityBookingsPage, activityBookingsPageSize]);

  useEffect(() => {
    if (!viewingActivityCompany?.id || activityTab !== 'customers') return;
    let isActive = true;
    setIsLoadingActivityCustomers(true);
    setActivityCustomersError('');
    api
      .listCompanyCustomers(viewingActivityCompany.id, {
        page: activityCustomersPage,
        pageSize: activityCustomersPageSize,
        q: activityCustomerSearch || undefined,
        sortBy: 'lastVisitAt',
        sortDir: 'desc'
      })
      .then((data) => {
        if (!isActive) return;
        setActivityCustomers(data.items || []);
        setActivityCustomersTotalCount(Number(data.totalCount || 0));
      })
      .catch((err: unknown) => {
        if (!isActive) return;
        setActivityCustomers([]);
        setActivityCustomersTotalCount(0);
        setActivityCustomersError(normalizeErrorMessage(err, 'Failed to load customers.'));
      })
      .finally(() => {
        if (isActive) setIsLoadingActivityCustomers(false);
      });

    return () => {
      isActive = false;
    };
  }, [
    viewingActivityCompany?.id,
    activityTab,
    activityCustomersPage,
    activityCustomersPageSize,
    activityCustomerSearch
  ]);

  useEffect(() => {
    setActivityCustomersPage(1);
  }, [activityCustomerSearch]);

  const refreshActiveCompanies = async () => {
    setIsLoadingCompanies(true);
    setCompaniesError('');
    try {
      const data = await api.listStaffCompanies({
        page,
        pageSize,
        q: search || undefined,
        status: 'active,on_hold,rejected',
        sortBy: companySort.sortBy,
        sortDir: companySort.sortDir
      });
      setCompanies(data.items || []);
      setTotalCount(Number(data.totalCount || 0));
    } catch (err: unknown) {
      setCompanies([]);
      setTotalCount(0);
      setCompaniesError(normalizeErrorMessage(err, 'Failed to load companies.'));
    } finally {
      setIsLoadingCompanies(false);
    }
  };

  const refreshPendingCompanies = async () => {
    setIsLoadingPending(true);
    setPendingError('');
    try {
      const data = await api.listStaffCompanies({
        page: pendingPage,
        pageSize: pendingPageSize,
        status: 'pending'
      });
      setPendingCompanies(data.items || []);
      setPendingTotalCount(Number(data.totalCount || 0));
    } catch (err: unknown) {
      setPendingCompanies([]);
      setPendingTotalCount(0);
      setPendingError(normalizeErrorMessage(err, 'Failed to load pending companies.'));
    } finally {
      setIsLoadingPending(false);
    }
  };

  const refreshTeamMembers = async () => {
    if (!viewingTeamCompany?.id) return;
    setIsLoadingTeam(true);
    setTeamError('');
    try {
      const data = await api.listCompanyTeam(viewingTeamCompany.id, {
        page: teamPage,
        pageSize: teamPageSize
      });
      setTeamMembers(data.items || []);
      setTeamTotalCount(Number(data.totalCount || 0));
    } catch (err: unknown) {
      setTeamMembers([]);
      setTeamTotalCount(0);
      setTeamError(normalizeErrorMessage(err, 'Failed to load company team.'));
    } finally {
      setIsLoadingTeam(false);
    }
  };

  const refreshCompanies = async () => {
    await Promise.all([refreshActiveCompanies(), refreshPendingCompanies()]);
  };

  const handleAddTeamMember = async () => {
    if (!viewingTeamCompany?.id) return;
    const name = newMember.name.trim();
    const email = newMember.email.trim().toLowerCase();
    if (!name || !email) {
      setMemberError('Please provide both name and email.');
      return;
    }

    setIsSavingMember(true);
    setMemberError('');
    try {
      await api.createCompanyMember(viewingTeamCompany.id, {
        name,
        email,
        receiveNotifications: newMember.receiveNotifications
      });
      showAlertModal('Team member added! A temporary password has been emailed to them.');
      setIsAddingTeamMember(false);
      setNewMember({ name: '', email: '', receiveNotifications: true });
      await refreshTeamMembers();
      await refreshCompanies();
    } catch (e: unknown) {
      setMemberError(normalizeErrorMessage(e, 'Failed to add team member.'));
    } finally {
      setIsSavingMember(false);
    }
  };

  const handleSaveTeamMember = async () => {
    if (!editingTeamMember) return;
    const name = editingTeamMember.name.trim();
    const email = editingTeamMember.email.trim().toLowerCase();
    if (!name || !email) {
      setTeamEditError('Name and email are required.');
      return;
    }
    if (!isValidEmail(email)) {
      setTeamEditError('Please enter a valid email address.');
      return;
    }

    setIsSavingTeamEdit(true);
    setTeamEditError('');
    try {
      await api.updateCompanyMember(editingTeamMember.id, {
        name,
        email,
        receiveNotifications: editingTeamMember.receiveNotifications
      });
      await refreshTeamMembers();
      await refreshCompanies();
      setEditingTeamMember(null);
    } catch (e: unknown) {
      setTeamEditError(normalizeErrorMessage(e, 'Failed to update team member.'));
    } finally {
      setIsSavingTeamEdit(false);
    }
  };

  const handleResetTeamMemberPassword = async (member: CompanyUser) => {
    const confirmed = await showConfirmModal(
      `Send a password reset email to ${member.name} (${member.email})?`,
      {
        title: 'Send Password Reset',
        confirmText: 'Send',
        cancelText: 'Cancel'
      }
    );
    if (!confirmed) return;

    setResettingMemberId(member.id);
    try {
      await api.resetCompanyMemberPassword(member.id);
      showAlertModal('Reset email sent.');
    } catch (e: unknown) {
      showAlertModal(normalizeErrorMessage(e, 'Failed to send reset email.'));
    } finally {
      setResettingMemberId(null);
    }
  };

  const handleDeleteTeamMember = async (memberId: string) => {
    const confirmed = await showConfirmModal(
      'Are you sure you want to revoke dashboard access for this user?',
      {
        title: 'Revoke Access',
        confirmText: 'Revoke',
        cancelText: 'Cancel'
      }
    );
    if (!confirmed) return;

    setIsDeletingMemberId(memberId);
    try {
      await api.deleteCompanyMember(memberId);
      await refreshTeamMembers();
      await refreshCompanies();
    } catch (e: unknown) {
      showAlertModal(normalizeErrorMessage(e, 'Failed to delete team member.'));
    } finally {
      setIsDeletingMemberId(null);
    }
  };

  const toggleCompanyNameSort = () => {
    setCompanySort((prev) => {
      if (prev.sortBy !== 'name') return { sortBy: 'name', sortDir: 'asc' };
      if (prev.sortDir === 'asc') return { sortBy: 'name', sortDir: 'desc' };
      return { sortBy: 'createdAt', sortDir: 'desc' };
    });
  };

  const renderCompanyNameSortIcon = () => {
    if (companySort.sortBy !== 'name') {
      return <ArrowUpDown className="w-3.5 h-3.5 text-slate-400" />;
    }
    return companySort.sortDir === 'asc' ? (
      <ArrowUp className="w-3.5 h-3.5 text-slate-600" />
    ) : (
      <ArrowDown className="w-3.5 h-3.5 text-slate-600" />
    );
  };

  const editingPhoneValue = String(editingCompany?.phone || '').trim();
  const showCompanyPhoneError =
    companyPhoneTouched && editingPhoneValue && !isValidUkPhone(editingPhoneValue);

  const getAddOnNames = (booking: Appointment) =>
    (booking.addOnIds || [])
      .map((id) => addOnsById.get(id)?.name)
      .filter(Boolean)
      .join(', ');

  const getApprovalDetails = (company: Company) => {
    const logs = [...(company.changeLogs || [])].sort(
      (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
    );
    const approvalLog = logs.find((l) => l.field === 'Status' && l.newValue === 'active');
    const tierLog = logs.find((l) => l.field === 'Tier');

    const createdDate = formatUkDateTime(company.createdAt, '—');

    return {
      approvedBy: approvalLog ? approvalLog.actor : 'System/Import',
      approvedAt: approvalLog ? formatUkDateTime(approvalLog.timestamp, '—') : createdDate,
      tierSetBy: tierLog ? tierLog.actor : approvalLog ? approvalLog.actor : 'System/Import',
      tierSetAt: tierLog
        ? formatUkDateTime(tierLog.timestamp, '—')
        : approvalLog
          ? formatUkDateTime(approvalLog.timestamp, '—')
          : createdDate
    };
  };

  const getStatusChangeDetails = (company: Company) => {
    const logs = [...(company.changeLogs || [])].sort(
      (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
    );
    const statusLog = logs.find((l) => l.field === 'Status');

    if (statusLog) {
      return {
        actor: statusLog.actor,
        date: formatUkDateTime(statusLog.timestamp, '—')
      };
    }
    return { actor: 'System/Import', date: formatUkDateTime(company.createdAt, '—') };
  };

  const toggleVenueRestriction = (venueId: string) => {
    if (!editingCompany) return;
    const currentAllowed = editingCompany.allowedVenueIds || [];

    let newAllowed: string[] = [];
    if (currentAllowed.includes(venueId)) {
      newAllowed = currentAllowed.filter((id) => id !== venueId);
    } else {
      newAllowed = [...currentAllowed, venueId];
    }

    setEditingCompany({ ...editingCompany, allowedVenueIds: newAllowed });
  };

  const handleDeleteCompany = async (company: Company) => {
    if (!onDeleteCompany) return;

    const confirmed = await showConfirmModal(
      `Delete "${company.name}" and all of its related records (bookings, forms, certificates, payments, and team users)? This cannot be undone.`,
      {
        title: 'Delete Company',
        confirmText: 'Delete',
        cancelText: 'Cancel'
      }
    );
    if (!confirmed) return;

    setIsDeletingCompanyId(company.id);
    try {
      await Promise.resolve(onDeleteCompany(company.id));
      await refreshCompanies();
    } catch (e: unknown) {
      showAlertModal(normalizeErrorMessage(e, 'Failed to delete company.'));
    } finally {
      setIsDeletingCompanyId(null);
    }
  };

  return (
    <div className="animate-fade-in space-y-8">
      {/* Activity Modal */}
      {viewingActivityCompany && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl overflow-hidden animate-fade-in flex flex-col max-h-[85vh]">
            <div className="p-6 border-b bg-slate-50 flex justify-between items-center">
              <div className="flex items-center gap-3">
                <div className="bg-slate-100 p-2 rounded-lg text-slate-600">
                  <Calendar className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-bold text-lg text-slate-900">
                    {viewingActivityCompany.name} Activity
                  </h3>
                  <p className="text-xs text-slate-500">
                    Bookings and unique customers linked to this company
                  </p>
                </div>
              </div>
              <button
                onClick={() => setViewingActivityCompany(null)}
                className="text-slate-400 hover:text-slate-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto space-y-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setActivityTab('bookings')}
                    className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${activityTab === 'bookings' ? 'bg-teal-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
                  >
                    Bookings ({activityBookingsTotalCount})
                  </button>
                  <button
                    onClick={() => setActivityTab('customers')}
                    className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${activityTab === 'customers' ? 'bg-teal-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
                  >
                    Customers ({activityCustomersTotalCount})
                  </button>
                </div>
                {activityTab === 'customers' && (
                  <div className="relative">
                    <Search className="absolute left-3 top-2.5 text-slate-400 w-4 h-4" />
                    <input
                      type="text"
                      placeholder="Search customers..."
                      className="pl-9 pr-3 py-2 border rounded-lg text-sm bg-white focus:ring-2 focus:ring-teal-500 outline-none"
                      value={activityCustomerSearch}
                      onChange={(e) => setActivityCustomerSearch(e.target.value)}
                    />
                  </div>
                )}
              </div>

              {activityTab === 'bookings' ? (
                <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                  {isLoadingActivityBookings ? (
                    <div className="p-6 text-center text-slate-500">
                      <Loader2 className="w-5 h-5 animate-spin inline-block mr-2" />
                      Loading bookings...
                    </div>
                  ) : activityBookingsError ? (
                    <div className="p-6 text-center text-red-500">{activityBookingsError}</div>
                  ) : activityBookings.length === 0 ? (
                    <div className="p-6 text-center text-slate-500 italic">
                      No bookings found for this company.
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-left text-sm">
                        <thead className="bg-slate-50 text-[10px] uppercase tracking-widest text-slate-500">
                          <tr>
                            <th className="px-4 py-3">Date</th>
                            <th className="px-4 py-3">Patient</th>
                            <th className="px-4 py-3">Service</th>
                            <th className="px-4 py-3">Add-ons</th>
                            <th className="px-4 py-3 text-center">Status</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {activityBookings.map((booking) => (
                            <tr key={booking.id} className="hover:bg-slate-50">
                              <td className="px-4 py-3 text-slate-600">
                                <div className="font-medium">{formatUkDate(booking.date, '—')}</div>
                                <div className="text-xs text-slate-400">{booking.time}</div>
                              </td>
                              <td className="px-4 py-3 text-slate-700">
                                <div className="font-medium">{booking.customerName}</div>
                                <div className="text-xs text-slate-400">
                                  {booking.customerEmail}
                                </div>
                              </td>
                              <td className="px-4 py-3 text-slate-600">
                                {booking.serviceName || booking.serviceId || '—'}
                              </td>
                              <td className="px-4 py-3 text-slate-600">
                                {getAddOnNames(booking) || '—'}
                              </td>
                              <td className="px-4 py-3 text-center">
                                <StatusBadge status={booking.status} />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              ) : (
                <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                  {isLoadingActivityCustomers ? (
                    <div className="p-6 text-center text-slate-500">
                      <Loader2 className="w-5 h-5 animate-spin inline-block mr-2" />
                      Loading customers...
                    </div>
                  ) : activityCustomersError ? (
                    <div className="p-6 text-center text-red-500">{activityCustomersError}</div>
                  ) : activityCustomers.length === 0 ? (
                    <div className="p-6 text-center text-slate-500 italic">
                      No customers found for this company.
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-left text-sm">
                        <thead className="bg-slate-50 text-[10px] uppercase tracking-widest text-slate-500">
                          <tr>
                            <th className="px-4 py-3">Customer</th>
                            <th className="px-4 py-3">Email</th>
                            <th className="px-4 py-3">Phone</th>
                            <th className="px-4 py-3 text-center">Visits</th>
                            <th className="px-4 py-3">Last Visit</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {activityCustomers.map((customer) => (
                            <tr key={`${customer.email}-${customer.id || ''}`}>
                              <td className="px-4 py-3 font-medium text-slate-700">
                                {customer.name || customer.email}
                              </td>
                              <td className="px-4 py-3 text-slate-600">{customer.email}</td>
                              <td className="px-4 py-3 text-slate-600">{customer.phone || '—'}</td>
                              <td className="px-4 py-3 text-center">
                                <span className="inline-flex items-center justify-center min-w-[40px] px-2 py-1 text-[10px] font-bold uppercase bg-slate-100 text-slate-600 rounded-full border border-slate-200">
                                  {customer.visitCount}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-slate-600">
                                {customer.lastVisitAt
                                  ? formatUkDate(customer.lastVisitAt, '—')
                                  : '—'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="p-4 border-t bg-slate-50">
              {activityTab === 'bookings' ? (
                <Pagination
                  page={activityBookingsPage}
                  pageSize={activityBookingsPageSize}
                  totalCount={activityBookingsTotalCount}
                  onPageChange={setActivityBookingsPage}
                  onPageSizeChange={(size) => {
                    setActivityBookingsPageSize(size);
                    setActivityBookingsPage(1);
                  }}
                  isLoading={isLoadingActivityBookings}
                />
              ) : (
                <Pagination
                  page={activityCustomersPage}
                  pageSize={activityCustomersPageSize}
                  totalCount={activityCustomersTotalCount}
                  onPageChange={setActivityCustomersPage}
                  onPageSizeChange={(size) => {
                    setActivityCustomersPageSize(size);
                    setActivityCustomersPage(1);
                  }}
                  isLoading={isLoadingActivityCustomers}
                />
              )}
            </div>
          </div>
        </div>
      )}

      {/* Team View Modal */}
      {viewingTeamCompany && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden animate-fade-in flex flex-col max-h-[80vh]">
            <div className="p-6 border-b bg-slate-50 flex justify-between items-center">
              <div className="flex items-center gap-3">
                <div className="bg-purple-100 p-2 rounded-lg text-purple-600">
                  <Users className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="font-bold text-lg text-slate-900">
                    {viewingTeamCompany.name} Team
                  </h3>
                  <p className="text-xs text-slate-500">Secondary users with dashboard access</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                {canEdit && (
                  <button
                    onClick={() => {
                      setIsAddingTeamMember(true);
                      setMemberError('');
                    }}
                    className="bg-purple-600 text-white px-3 py-1.5 rounded-lg text-xs font-bold hover:bg-purple-700 flex items-center gap-2 shadow-md"
                  >
                    <UserPlus className="w-4 h-4" /> Add Team Member
                  </button>
                )}
                <button
                  onClick={() => setViewingTeamCompany(null)}
                  className="text-slate-400 hover:text-slate-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div className="p-6 overflow-y-auto">
              <div className="space-y-3">
                <div className="bg-slate-50 p-4 rounded-xl border border-slate-100 flex justify-between items-center">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-slate-200 rounded-full flex items-center justify-center text-slate-500 font-bold uppercase">
                      {viewingTeamCompany.contactPerson.charAt(0)}
                    </div>
                    <div>
                      <div className="font-bold text-slate-900">
                        {viewingTeamCompany.contactPerson}
                      </div>
                      <div className="text-sm text-slate-500">{viewingTeamCompany.email}</div>
                    </div>
                  </div>
                  <span className="text-[10px] font-black uppercase tracking-widest text-slate-400 border border-slate-200 px-2 py-1 rounded bg-white">
                    Primary Account
                  </span>
                </div>

                {isLoadingTeam ? (
                  <div className="text-center py-10 border-2 border-dashed border-slate-100 rounded-xl">
                    <Loader2 className="w-5 h-5 animate-spin inline-block mr-2 text-slate-400" />
                    <span className="text-sm text-slate-400 italic">Loading team members...</span>
                  </div>
                ) : teamError ? (
                  <div className="text-center py-10 border-2 border-dashed border-slate-100 rounded-xl text-red-500">
                    {teamError}
                  </div>
                ) : teamMembers.length === 0 ? (
                  <div className="text-center py-10 border-2 border-dashed border-slate-100 rounded-xl">
                    <p className="text-sm text-slate-400 italic">
                      No secondary team members found for this company.
                    </p>
                  </div>
                ) : (
                  teamMembers.map((member) => (
                    <div
                      key={member.id}
                      className="bg-white p-4 rounded-xl border border-slate-200 flex justify-between items-center hover:border-purple-200 transition-all group"
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 bg-purple-50 rounded-full flex items-center justify-center text-purple-600 font-bold uppercase">
                          {member.name.charAt(0)}
                        </div>
                        <div>
                          <div className="font-bold text-slate-800">{member.name}</div>
                          <div className="text-sm text-slate-500">{member.email}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {member.mustChangePassword && (
                          <span className="text-[9px] font-bold text-amber-600 bg-amber-50 px-2 py-1 rounded-full">
                            Password Change Required
                          </span>
                        )}
                        <button
                          onClick={() => setEditingTeamMember({ ...member })}
                          className="p-2 text-slate-300 hover:text-teal-600 hover:bg-teal-50 rounded-lg transition-all"
                          title="Edit Team Member"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleResetTeamMemberPassword(member)}
                          disabled={resettingMemberId === member.id}
                          className="p-2 text-slate-300 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-all"
                          title="Send Reset Email"
                        >
                          {resettingMemberId === member.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Key className="w-4 h-4" />
                          )}
                        </button>
                        <button
                          onClick={() => handleDeleteTeamMember(member.id)}
                          disabled={isDeletingMemberId === member.id}
                          className="p-2 text-slate-300 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
                          title="Revoke Access"
                        >
                          {isDeletingMemberId === member.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Trash2 className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
              <Pagination
                page={teamPage}
                pageSize={teamPageSize}
                totalCount={teamTotalCount}
                onPageChange={setTeamPage}
                onPageSizeChange={(size) => {
                  setTeamPageSize(size);
                  setTeamPage(1);
                }}
                isLoading={isLoadingTeam}
              />
            </div>
            <div className="p-6 border-t bg-slate-50 flex justify-end">
              <button
                onClick={() => setViewingTeamCompany(null)}
                className="bg-slate-900 text-white px-6 py-2 rounded-xl font-bold hover:bg-slate-800 transition-all"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* New Team Member Modal */}
      {isAddingTeamMember && viewingTeamCompany && canRenderPortal
        ? createPortal(
            <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[100] p-4">
              <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-fade-in">
                <div className="p-6 bg-white flex justify-between items-center">
                  <h3 className="font-bold text-lg text-slate-800 flex items-center gap-2">
                    <UserPlus className="w-5 h-5 text-purple-600" /> Add Team Member
                  </h3>
                  <button onClick={() => setIsAddingTeamMember(false)}>
                    <X className="w-5 h-5 text-slate-400" />
                  </button>
                </div>
                <div className="p-6 space-y-6">
                  <div className="bg-amber-50 border border-amber-100 p-4 rounded-xl">
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                      <div className="text-sm text-amber-800">
                        <p className="font-bold">Important Security Warning</p>
                        <p className="mt-1">
                          You are granting this person <strong>full dashboard access</strong> for{' '}
                          {viewingTeamCompany.name}.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-xs font-bold text-slate-500 uppercase mb-1">
                        Full Name
                      </label>
                      <input
                        className="w-full border rounded-xl p-3 bg-slate-50 outline-none focus:ring-2 focus:ring-purple-500"
                        placeholder="Team Member Name"
                        value={newMember.name}
                        onChange={(e) => setNewMember({ ...newMember, name: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-slate-500 uppercase mb-1">
                        Email Address
                      </label>
                      <input
                        type="email"
                        className="w-full border rounded-xl p-3 bg-slate-50 outline-none focus:ring-2 focus:ring-purple-500"
                        placeholder="member@company.com"
                        value={newMember.email}
                        onChange={(e) => setNewMember({ ...newMember, email: e.target.value })}
                      />
                    </div>
                  </div>

                  {memberError && (
                    <div className="text-red-600 text-sm font-medium bg-red-50 p-2 rounded">
                      {memberError}
                    </div>
                  )}
                </div>
                <div className="p-6 border-t bg-slate-50 flex justify-end gap-3">
                  <button
                    onClick={() => setIsAddingTeamMember(false)}
                    className="px-6 py-2 text-slate-600 font-bold"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleAddTeamMember}
                    disabled={isSavingMember}
                    className="bg-purple-600 text-white px-8 py-2 rounded-xl font-bold hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2 shadow-lg shadow-purple-600/20"
                  >
                    {isSavingMember ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <ShieldCheck className="w-4 h-4" />
                    )}
                    Grant Access
                  </button>
                </div>
              </div>
            </div>,
            document.body
          )
        : null}

      {/* Edit Team Member Modal */}
      {editingTeamMember && viewingTeamCompany && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-fade-in">
            <div className="p-6 border-b bg-slate-50 flex justify-between items-center">
              <h3 className="font-bold text-lg text-slate-800 flex items-center gap-2">
                <Edit2 className="w-5 h-5 text-teal-600" /> Edit Team Member
              </h3>
              <button onClick={() => setEditingTeamMember(null)}>
                <X className="w-5 h-5 text-slate-400" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase mb-1">
                  Full Name
                </label>
                <input
                  className="w-full border rounded-xl p-3 bg-slate-50 outline-none focus:ring-2 focus:ring-teal-500"
                  placeholder="Team Member Name"
                  value={editingTeamMember.name}
                  onChange={(e) =>
                    setEditingTeamMember({ ...editingTeamMember, name: e.target.value })
                  }
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase mb-1">
                  Email Address
                </label>
                <input
                  type="email"
                  className="w-full border rounded-xl p-3 bg-slate-50 outline-none focus:ring-2 focus:ring-teal-500"
                  placeholder="member@company.com"
                  value={editingTeamMember.email}
                  onChange={(e) =>
                    setEditingTeamMember({ ...editingTeamMember, email: e.target.value })
                  }
                />
                <p className="mt-1 text-[10px] text-slate-400">
                  Changing email updates the login email for this user.
                </p>
              </div>

              {teamEditError && (
                <div className="text-red-600 text-sm font-medium bg-red-50 p-2 rounded">
                  {teamEditError}
                </div>
              )}
            </div>
            <div className="p-6 border-t bg-slate-50 flex justify-end gap-3">
              <button
                onClick={() => setEditingTeamMember(null)}
                className="px-6 py-2 text-slate-600 font-bold"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveTeamMember}
                disabled={isSavingTeamEdit}
                className="bg-teal-600 text-white px-8 py-2 rounded-xl font-bold hover:bg-teal-700 disabled:opacity-50 flex items-center gap-2 shadow-lg shadow-teal-600/20"
              >
                {isSavingTeamEdit ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <ShieldCheck className="w-4 h-4" />
                )}
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Approve Modal */}
      {approvingCompany && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-sm w-full animate-fade-in">
            <h3 className="font-bold text-lg text-slate-900 mb-2">Approve Company</h3>
            <p className="text-slate-500 text-sm mb-4">
              Set up account details for {approvingCompany.name}.
            </p>

            <div className="mb-4 space-y-3">
              <div>
                <label className="block text-sm font-medium mb-1">Pricing Tier</label>
                <select
                  className="w-full border rounded p-2 bg-white text-sm"
                  value={selectedTier}
                  onChange={(e) => setSelectedTier(e.target.value as PricingTier)}
                >
                  <option value="bronze">Bronze (Standard)</option>
                  <option value="silver">Silver (Discounted)</option>
                  <option value="gold">Gold (Premium)</option>
                  <option value="platinum">Platinum (VIP)</option>
                  <option value="diamond">Diamond (Partner)</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Payment Type</label>
                <select
                  className="w-full border rounded p-2 bg-white text-sm"
                  value={selectedPaymentPref}
                  onChange={(e) => {
                    const val = e.target.value as 'invoice' | 'card';
                    setSelectedPaymentPref(val);
                    if (val === 'card') setSelectedRequirePo(false);
                  }}
                >
                  <option value="invoice">Invoice Later (No upfront payment)</option>
                  <option value="card">Card Payment (Charge per booking)</option>
                </select>
              </div>

              {selectedPaymentPref === 'invoice' && (
                <div className="bg-blue-50/50 p-3 rounded-lg border border-blue-100 mt-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      className="w-4 h-4 text-teal-600 rounded"
                      checked={selectedRequirePo}
                      onChange={(e) => setSelectedRequirePo(e.target.checked)}
                    />
                    <span className="text-xs font-bold text-blue-900">Mandatory PO Number</span>
                  </label>
                  <p className="text-[10px] text-blue-600 mt-1 ml-6">
                    Forcing a PO ensures cleaner reconciliation for invoice-based clients.
                  </p>
                </div>
              )}
            </div>

            <div className="bg-slate-50 p-3 rounded-lg border border-slate-200 mb-6">
              <div className="text-sm text-slate-700 flex items-center gap-1">
                <Mail className="w-3 h-3 text-slate-500" />
                "Account Approved" Email will be sent automatically.
              </div>
            </div>

            <div className="flex justify-end gap-2">
              <button
                onClick={() => setApprovingCompany(null)}
                className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  if (onApproveCompany) {
                    await Promise.resolve(
                      onApproveCompany(
                        approvingCompany.id,
                        selectedTier,
                        selectedPaymentPref,
                        selectedRequirePo
                      )
                    );
                  }
                  setApprovingCompany(null);
                  await refreshCompanies();
                }}
                className="bg-teal-600 text-white px-4 py-2 rounded hover:bg-teal-700 font-medium"
              >
                Confirm Approval
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {rejectingCompany && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-sm w-full animate-fade-in">
            <div className="flex items-center gap-2 text-red-600 mb-2">
              <AlertCircle className="w-6 h-6" />
              <h3 className="font-bold text-lg">Reject Application</h3>
            </div>
            <p className="text-slate-500 text-sm mb-4">
              Are you sure you want to reject the application from{' '}
              <strong>{rejectingCompany.name}</strong>? This cannot be undone.
            </p>

            <div className="bg-slate-50 p-3 rounded-lg border border-slate-200 mb-6">
              <div className="text-sm text-slate-700 flex items-center gap-1">
                <Mail className="w-3 h-3 text-slate-500" />
                "Application Rejected" Email will be sent automatically.
              </div>
            </div>

            <div className="flex justify-end gap-2">
              <button
                onClick={() => setRejectingCompany(null)}
                className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  if (onRejectCompany) {
                    await Promise.resolve(onRejectCompany(rejectingCompany.id));
                  }
                  setRejectingCompany(null);
                  await refreshCompanies();
                }}
                className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 font-medium"
              >
                Reject Application
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit/Add Modal - Updated size to max-sm for a tighter UI */}
      {editingCompany && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-sm w-full animate-fade-in max-h-[90vh] overflow-y-auto">
            <h3 className="font-bold text-lg text-slate-900 mb-4">
              {editingCompany.id ? 'Edit Company' : 'Add Company'}
            </h3>

            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">
                  Company Name
                </label>
                <input
                  className="w-full border p-2 rounded bg-white"
                  value={editingCompany.name}
                  onChange={(e) => setEditingCompany({ ...editingCompany, name: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">
                  Contact Person
                </label>
                <input
                  className="w-full border p-2 rounded bg-white"
                  value={editingCompany.contactPerson}
                  onChange={(e) =>
                    setEditingCompany({ ...editingCompany, contactPerson: e.target.value })
                  }
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">
                  Company Address
                </label>
                <input
                  className="w-full border p-2 rounded bg-white"
                  value={editingCompany.address}
                  onChange={(e) =>
                    setEditingCompany({ ...editingCompany, address: e.target.value })
                  }
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">
                  Phone Number
                </label>
                <input
                  className="w-full border p-2 rounded bg-white"
                  value={editingCompany.phone || ''}
                  onChange={(e) => setEditingCompany({ ...editingCompany, phone: e.target.value })}
                  onBlur={(e) => {
                    const value = e.target.value.trim();
                    setCompanyPhoneTouched(true);
                    if (value && isValidUkPhone(value)) {
                      setEditingCompany({ ...editingCompany, phone: normalizeUkPhone(value) });
                    }
                  }}
                  placeholder="e.g. 01234 567890"
                />
                <p className="text-[10px] text-slate-400 mt-1">
                  UK numbers only, e.g. 07123 456789 or +44 7123 456789.
                </p>
                {showCompanyPhoneError && (
                  <p className="mt-1 text-xs font-semibold text-red-600">
                    Enter a valid UK phone number.
                  </p>
                )}
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Email</label>
                <input
                  className="w-full border p-2 rounded bg-white"
                  value={editingCompany.email}
                  onChange={(e) => setEditingCompany({ ...editingCompany, email: e.target.value })}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1">
                    Pricing Tier
                  </label>
                  <select
                    className="w-full border rounded p-2 bg-white"
                    value={editingCompany.tier}
                    onChange={(e) =>
                      setEditingCompany({ ...editingCompany, tier: e.target.value as PricingTier })
                    }
                  >
                    <option value="bronze">Bronze</option>
                    <option value="silver">Silver</option>
                    <option value="gold">Gold</option>
                    <option value="platinum">Platinum</option>
                    <option value="diamond">Diamond</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1">
                    Payment Method
                  </label>
                  <select
                    className="w-full border rounded p-2 bg-white"
                    value={editingCompany.paymentPreference || 'invoice'}
                    onChange={(e) => {
                      const val = e.target.value as 'invoice' | 'card';
                      setEditingCompany({
                        ...editingCompany,
                        paymentPreference: val,
                        requirePo: val === 'card' ? false : editingCompany.requirePo
                      });
                    }}
                  >
                    <option value="invoice">Invoice</option>
                    <option value="card">Card / Charge</option>
                  </select>
                </div>
              </div>

              {editingCompany.paymentPreference === 'invoice' && (
                <div className="bg-blue-50/50 p-3 rounded-lg border border-blue-100 flex justify-between items-center">
                  <div>
                    <div className="text-xs font-bold text-blue-900">Force PO Number</div>
                    <p className="text-[10px] text-blue-500">
                      Prevent bookings without a reference.
                    </p>
                  </div>
                  <input
                    type="checkbox"
                    className="w-5 h-5 text-teal-600 rounded"
                    checked={!!editingCompany.requirePo}
                    onChange={(e) =>
                      setEditingCompany({ ...editingCompany, requirePo: e.target.checked })
                    }
                  />
                </div>
              )}

              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Status</label>
                <select
                  className="w-full border rounded p-2 bg-white"
                  value={editingCompany.status}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (isCompanyStatus(value)) {
                      setEditingCompany({ ...editingCompany, status: value });
                    }
                  }}
                >
                  <option value="pending">Pending</option>
                  <option value="active">Active</option>
                  <option value="on_hold">On Hold</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>

              {/* Staff Notes Feature - Admin/Staff Use Only */}
              <div className="bg-amber-50 p-3 rounded-lg border border-amber-200">
                <label className="block text-xs font-bold text-amber-900 mb-1.5 flex items-center gap-2">
                  <MessageSquare className="w-3.5 h-3.5" /> Internal Staff Notes
                </label>
                <textarea
                  className="w-full border p-2 rounded bg-white text-xs min-h-[100px] outline-none focus:ring-1 focus:ring-amber-500"
                  placeholder="Private notes about this company account... (Pricing deals, key contacts, billing nuances)"
                  value={editingCompany.staffNotes || ''}
                  onChange={(e) =>
                    setEditingCompany({ ...editingCompany, staffNotes: e.target.value })
                  }
                />
                <p className="text-[9px] text-amber-600 mt-1 italic">
                  Visible to staff only. The company cannot see these.
                </p>
              </div>

              <div className="pt-2">
                <label className="block text-xs font-medium text-slate-500 mb-2">
                  Allowed Locations
                </label>
                <div className="border rounded-lg max-h-40 overflow-y-auto p-2 bg-slate-50 space-y-1">
                  {venues.length > 0 ? (
                    venues.map((venue) => (
                      <label
                        key={venue.id}
                        className="flex items-center gap-2 cursor-pointer hover:bg-slate-100 p-1.5 rounded"
                      >
                        <input
                          type="checkbox"
                          className="w-4 h-4 text-teal-600 rounded"
                          checked={
                            editingCompany.allowedVenueIds &&
                            editingCompany.allowedVenueIds.length > 0
                              ? editingCompany.allowedVenueIds.includes(venue.id)
                              : true
                          }
                          onChange={() => toggleVenueRestriction(venue.id)}
                        />
                        <span className="text-sm text-slate-700">
                          {venue.name}, {venue.city}
                        </span>
                      </label>
                    ))
                  ) : (
                    <p className="text-xs text-slate-400 p-2">No venues configured in system.</p>
                  )}
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-6 pt-4 border-t">
              <button
                onClick={() => setEditingCompany(null)}
                className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  try {
                    if (editingCompany.id) {
                      const rawPhone = String(editingCompany.phone || '').trim();
                      if (rawPhone && !isValidUkPhone(rawPhone)) {
                        setCompanyPhoneTouched(true);
                        showAlertModal('Please enter a valid UK phone number.');
                        return;
                      }
                      const normalizedPhone = rawPhone ? normalizeUkPhone(rawPhone) : '';
                      const payload: Company = {
                        ...editingCompany,
                        phone: normalizedPhone,
                        status: editingCompany.status || 'active',
                        tier: editingCompany.tier || 'bronze',
                        paymentPreference: editingCompany.paymentPreference || 'invoice',
                        requirePo: Boolean(editingCompany.requirePo)
                      };
                      if (onUpdateCompany) await Promise.resolve(onUpdateCompany(payload));
                    } else if (onAddCompany) {
                      const rawPhone = String(editingCompany.phone || '').trim();
                      if (rawPhone && !isValidUkPhone(rawPhone)) {
                        setCompanyPhoneTouched(true);
                        showAlertModal('Please enter a valid UK phone number.');
                        return;
                      }
                      const normalizedPhone = rawPhone ? normalizeUkPhone(rawPhone) : '';
                      await Promise.resolve(
                        onAddCompany({
                          name: editingCompany.name,
                          contactPerson: editingCompany.contactPerson,
                          email: editingCompany.email,
                          address: editingCompany.address,
                          id: createId(),
                          createdAt: new Date().toISOString(),
                          phone: normalizedPhone,
                          paymentPreference: editingCompany.paymentPreference || 'invoice',
                          tier: editingCompany.tier || 'bronze',
                          status: editingCompany.status || 'active',
                          requirePo: Boolean(editingCompany.requirePo),
                          allowedVenueIds: editingCompany.allowedVenueIds,
                          staffNotes: editingCompany.staffNotes,
                          changeLogs: []
                        } as Company)
                      );
                    }
                    setEditingCompany(null);
                    await refreshCompanies();
                  } catch (err: unknown) {
                    showAlertModal(normalizeErrorMessage(err, 'Failed to save company changes.'));
                  }
                }}
                className="bg-slate-900 text-white px-4 py-2 rounded hover:bg-slate-800"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Pending Approvals */}
      {canEdit && (isLoadingPending || pendingError || pendingCompanies.length > 0) && (
        <div className="bg-orange-50 border border-orange-200 rounded-xl p-6">
          <h3 className="text-lg font-bold text-orange-800 flex items-center gap-2 mb-4">
            <AlertCircle className="w-5 h-5" /> Pending Approvals ({pendingTotalCount})
          </h3>
          <div className="space-y-3">
            {isLoadingPending ? (
              <div className="bg-white p-6 rounded-lg border border-orange-100 text-center text-slate-500">
                <Loader2 className="w-5 h-5 animate-spin inline-block mr-2" />
                Loading pending companies...
              </div>
            ) : pendingError ? (
              <div className="bg-white p-6 rounded-lg border border-orange-100 text-center text-red-500">
                {pendingError}
              </div>
            ) : pendingCompanies.length === 0 ? (
              <div className="bg-white p-6 rounded-lg border border-orange-100 text-center text-slate-500 italic">
                No pending approvals right now.
              </div>
            ) : (
              pendingCompanies.map((company) => (
                <div
                  key={company.id}
                  className="bg-white p-4 rounded-lg shadow-sm border border-orange-100 flex justify-between items-center"
                >
                  <div>
                    <h4 className="font-bold text-slate-900">{company.name}</h4>
                    <p className="text-sm text-slate-500">
                      Contact: {company.contactPerson} ({company.email})
                    </p>
                    <p className="text-xs text-slate-400 mt-1">
                      Requested: {formatUkDateTime(company.createdAt, '—')}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        setRejectingCompany(company);
                      }}
                      disabled={isDeletingCompanyId === company.id}
                      className="text-red-600 px-4 py-2 text-sm font-medium hover:bg-red-50 rounded disabled:opacity-40"
                    >
                      Reject
                    </button>
                    <button
                      onClick={() => {
                        setApprovingCompany(company);
                        setSelectedTier('bronze'); // Reset default
                        setSelectedPaymentPref('invoice');
                        setSelectedRequirePo(false);
                      }}
                      disabled={isDeletingCompanyId === company.id}
                      className="bg-teal-600 text-white px-4 py-2 text-sm font-medium hover:bg-teal-700 rounded flex items-center gap-2 disabled:opacity-40"
                    >
                      <Check className="w-4 h-4" /> Approve
                    </button>
                    {onDeleteCompany && (
                      <button
                        onClick={() => handleDeleteCompany(company)}
                        disabled={isDeletingCompanyId === company.id}
                        className="text-red-700 border border-red-200 px-4 py-2 text-sm font-medium hover:bg-red-50 rounded flex items-center gap-2 disabled:opacity-40"
                      >
                        {isDeletingCompanyId === company.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Trash2 className="w-4 h-4" />
                        )}
                        Delete
                      </button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
          <Pagination
            page={pendingPage}
            pageSize={pendingPageSize}
            totalCount={pendingTotalCount}
            onPageChange={setPendingPage}
            onPageSizeChange={(size) => {
              setPendingPageSize(size);
              setPendingPage(1);
            }}
            isLoading={isLoadingPending}
          />
        </div>
      )}

      {/* Active Companies */}
      <div>
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-bold text-slate-800">Company Accounts</h3>
          <div className="flex items-center gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-2.5 text-slate-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search companies..."
                className="pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 outline-none w-64 bg-white text-sm"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            {canEdit && (
              <button
                onClick={() =>
                  setEditingCompany({
                    id: '',
                    name: '',
                    contactPerson: '',
                    email: '',
                    address: '',
                    status: 'active',
                    tier: 'bronze',
                    paymentPreference: 'invoice',
                    requirePo: false,
                    allowedVenueIds: venues.map((v) => v.id),
                    createdAt: '',
                    changeLogs: []
                  })
                }
                className="bg-slate-900 text-white px-4 py-2 rounded-lg font-medium hover:bg-slate-800 flex items-center gap-2 text-sm"
              >
                <Plus className="w-4 h-4" /> Add Company
              </button>
            )}
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          <table className="w-full text-left">
            <thead className="bg-slate-50 border-b">
              <tr>
                <th className="p-4 font-medium text-slate-600 text-sm">
                  <button
                    type="button"
                    onClick={toggleCompanyNameSort}
                    className="inline-flex items-center gap-1.5 hover:text-slate-900"
                    title="Toggle company name sort"
                  >
                    Company Name
                    {renderCompanyNameSortIcon()}
                  </button>
                </th>
                <th className="p-4 font-medium text-slate-600 text-sm">Contact</th>
                <th className="p-4 font-medium text-slate-600 text-sm">Address</th>
                <th className="p-4 font-medium text-slate-600 text-sm">Tier & Terms</th>
                <th className="p-4 font-medium text-slate-600 text-sm">Status</th>
                {canEdit && (
                  <th className="p-4 font-medium text-slate-600 text-right text-sm">Actions</th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {isLoadingCompanies ? (
                <tr>
                  <td colSpan={6} className="p-8 text-center text-slate-500">
                    <Loader2 className="w-5 h-5 animate-spin inline-block mr-2" />
                    Loading companies...
                  </td>
                </tr>
              ) : companiesError ? (
                <tr>
                  <td colSpan={6} className="p-8 text-center text-red-500">
                    {companiesError}
                  </td>
                </tr>
              ) : companies.length === 0 ? (
                <tr>
                  <td colSpan={6} className="p-8 text-center text-slate-500 italic">
                    No active companies found.
                  </td>
                </tr>
              ) : (
                companies.map((company) => {
                  const { approvedBy, approvedAt, tierSetBy, tierSetAt } =
                    getApprovalDetails(company);
                  const statusDetails = getStatusChangeDetails(company);
                  const restrictedCount =
                    company.allowedVenueIds &&
                    company.allowedVenueIds.length > 0 &&
                    company.allowedVenueIds.length < venues.length
                      ? company.allowedVenueIds.length
                      : 'All';
                  const teamCount = Number(company.teamCount ?? company.teamMembers?.length ?? 0);

                  return (
                    <tr key={company.id} className="hover:bg-slate-50 transition-colors">
                      <td className="p-4 font-medium text-slate-900">
                        <div className="flex items-center gap-2">
                          {company.name}
                          {company.staffNotes && (
                            <div className="group/note relative">
                              <MessageSquare className="w-3.5 h-3.5 text-amber-500" />
                              <div className="absolute left-0 bottom-full mb-2 w-48 p-2 bg-slate-900 text-white text-[10px] rounded shadow-xl opacity-0 group-hover/note:opacity-100 pointer-events-none transition-opacity z-50">
                                {company.staffNotes}
                              </div>
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-1 mt-1 text-[10px] text-slate-500">
                          <MapPin className="w-3 h-3" /> Locations:{' '}
                          {restrictedCount === 'All' ? 'All' : `${restrictedCount} Allowed`}
                        </div>
                      </td>
                      <td className="p-4 text-slate-600 text-sm">
                        <div className="flex flex-col">
                          <span className="font-medium">{company.contactPerson}</span>
                          <span className="text-slate-400">{company.email}</span>
                        </div>
                      </td>
                      <td className="p-4 text-slate-600 text-sm">{company.address}</td>
                      <td className="p-4 overflow-visible">
                        <div className="relative group w-fit flex flex-col items-start gap-1">
                          <span
                            className={`cursor-help px-2 py-1 rounded text-[10px] font-bold uppercase ${
                              company.tier === 'gold'
                                ? 'bg-yellow-100 text-yellow-800 border border-yellow-200'
                                : company.tier === 'silver'
                                  ? 'bg-slate-100 text-slate-600 border border-slate-200'
                                  : company.tier === 'platinum'
                                    ? 'bg-indigo-100 text-indigo-800 border border-indigo-200'
                                    : company.tier === 'diamond'
                                      ? 'bg-cyan-100 text-cyan-800 border border-cyan-200'
                                      : 'bg-orange-50 text-orange-800 border border-orange-200'
                            }`}
                          >
                            {company.tier}
                          </span>
                          <div className="flex items-center gap-1.5">
                            <span className="text-[10px] text-slate-500 flex items-center gap-1">
                              {company.paymentPreference === 'card' ? (
                                <CreditCard className="w-3 h-3" />
                              ) : (
                                <FileText className="w-3 h-3" />
                              )}
                              {company.paymentPreference === 'card' ? 'Pay Now' : 'Invoice'}
                            </span>
                            {company.requirePo && (
                              <span className="text-[9px] bg-red-50 text-red-500 font-bold px-1 rounded border border-red-100">
                                PO REQ
                              </span>
                            )}
                          </div>

                          {/* Tier Tooltip */}
                          <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-max px-4 py-3 bg-slate-900 text-white text-xs rounded-xl shadow-xl invisible group-hover:visible opacity-0 group-hover:opacity-100 transition-all z-50 pointer-events-none">
                            <div className="space-y-3 text-left">
                              <div>
                                <p className="text-slate-400 text-[10px] uppercase font-bold mb-0.5">
                                  Approved By
                                </p>
                                <div className="font-medium text-white text-sm">{approvedBy}</div>
                                <div className="text-[10px] text-slate-500">{approvedAt}</div>
                              </div>
                              <div className="border-t border-slate-700 pt-2">
                                <p className="text-slate-400 text-[10px] uppercase font-bold mb-0.5">
                                  Tier Set By
                                </p>
                                <div className="font-medium text-white text-sm">{tierSetBy}</div>
                                <div className="text-[10px] text-slate-500">{tierSetAt}</div>
                              </div>
                            </div>
                            <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-slate-900"></div>
                          </div>
                        </div>
                      </td>
                      <td className="p-4 overflow-visible">
                        <div className="relative group w-fit">
                          <span
                            className={`cursor-help px-2 py-1 rounded-full text-[10px] font-bold uppercase ${
                              company.status === 'active'
                                ? 'bg-green-100 text-green-700'
                                : company.status === 'on_hold'
                                  ? 'bg-amber-100 text-amber-700'
                                  : 'bg-red-100 text-red-700'
                            }`}
                          >
                            {company.status.toUpperCase().replace('_', ' ')}
                          </span>
                          {/* Status Tooltip */}
                          <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-max px-3 py-2 bg-slate-900 text-white text-xs rounded-lg shadow-xl invisible group-hover:visible opacity-0 group-hover:opacity-100 transition-all z-50 pointer-events-none">
                            <div className="space-y-1">
                              <p>
                                <span className="text-slate-400">Changed by:</span>{' '}
                                <span className="font-medium text-white">
                                  {statusDetails.actor}
                                </span>
                              </p>
                              <p>
                                <span className="text-slate-400">Date:</span>{' '}
                                <span className="font-medium text-white">{statusDetails.date}</span>
                              </p>
                            </div>
                            <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-slate-900"></div>
                          </div>
                        </div>
                      </td>
                      {canEdit && (
                        <td className="p-4 text-right">
                          <div className="flex justify-end gap-2">
                            <button
                              onClick={() => setViewingActivityCompany(company)}
                              className="text-slate-600 p-2 hover:bg-slate-100 rounded transition-all"
                              title="View bookings and customers"
                            >
                              <Calendar className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => setViewingTeamCompany(company)}
                              className="text-purple-600 p-2 hover:bg-purple-50 rounded transition-all flex items-center gap-1 text-xs font-bold"
                              title="View Team"
                            >
                              <Users className="w-4 h-4" />{' '}
                              {teamCount > 0 && <span>({teamCount})</span>}
                            </button>
                            <button
                              onClick={() => setEditingCompany(normalizeEditableCompany(company))}
                              className="text-teal-600 p-2 hover:bg-slate-100 rounded transition-colors"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            {onDeleteCompany && (
                              <button
                                onClick={() => handleDeleteCompany(company)}
                                disabled={isDeletingCompanyId === company.id}
                                className="text-red-600 p-2 hover:bg-red-50 rounded transition-colors disabled:opacity-40"
                                title="Delete Company"
                              >
                                {isDeletingCompanyId === company.id ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <Trash2 className="w-4 h-4" />
                                )}
                              </button>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        <Pagination
          page={page}
          pageSize={pageSize}
          totalCount={totalCount}
          onPageChange={setPage}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPage(1);
          }}
          isLoading={isLoadingCompanies}
        />
      </div>
    </div>
  );
};
